import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withApollo, compose } from 'react-apollo';
import { withRouter } from 'react-router';
import get from 'lodash/get';
import {
  LoadingSpinner,
  Notifications,
  ToastNotifications,
} from 'legacy-packages/ft-cb';
import {
  authorize,
  addSession,
  endSession,
  authenticateStrategyAction,
  storeCbSessionAction,
  levityLoginAction,
  levityLogoutAction,
  bindLevityEventsAction,
  storeApolloClientAction,
} from 'legacy-src/components/auth/actions';
import {
  getCbSession,
  timeLeftUntilCbSessionTimeout,
  timeLeftUntilCBSessionExpire,
} from 'legacy-src/components/auth/utils';
import { bindLevityEvents } from 'legacy-src/components/auth/Levity';
import { pageLoading } from 'legacy-src/actions/commonActions';
import authenticate from 'legacy-src/utility/authenticate';
import SecureFirewallCheck from 'legacy-src/utility/FirewallCheck/SecureFirewallCheck';
import SplitIOHandler from 'chameleon/ui-stack/utilities/split-io-handler';
import ToastContentWrapper from 'chameleon-src/components/wrappers/ToastContentWrapper';
import ValidatedRoute from 'chameleon-src/utility/validatedRoute';

export default (WrappedComponent) => {
  class RequireAuth extends Component {
    static displayName = 'RequireAuth';

    constructor(props) {
      super(props);
      this.bindLevityEvents = bindLevityEvents.bind(this);
    }

    componentDidMount() {
      const isLocalDevelopment = process.env.ENV === 'dev';
      if (!isLocalDevelopment) {
        this.bindLevityEvents();
      }
      const {
        authenticateStrategy,
        storeCbSession,
        userData,
        storeApolloClient,
      } = this.props;
      const cbSessionExpiration = get(getCbSession(), 'createTimeInMS', null);
      const APROLink = new RegExp(ROS_FRONTEND_URL, 'g');
      const PLLink = new RegExp(CB_LEGACY_URL, 'g');
      const whiteListedReferrers =
        APROLink.test(document.referrer) ||
        PLLink.test(document.referrer) ||
        /apstudents.collegeboard.org/g.test(document.referrer) ||
        /apcentral.collegeboard.org/g.test(document.referrer);
      const initialCheckOccurred =
        window.localStorage.getItem('apc:initialExpirationCheck') === 'true';
      // if initial check already occurred, and coming from APROorMYAP, this condition is satisfied
      const isNotInitialCheckAndReferredFromAPRO =
        initialCheckOccurred && whiteListedReferrers;

      const flagHandler = new SplitIOHandler({
        key: get(userData, 'me.initId', 1),
        splitName: [
          'cb_session_expiration_threshold',
          // feature flag "cb_session_expiration_check" FORCES this check regardless of referrer
          'cb_session_expiration_check',
          'levity_use_createtime',
          'cb_session_expiration_override',
        ],
        callback: (treatment) => {
          if (cbSessionExpiration !== null) {
            // if it is the initial check, we set local storage to ensure we don't do it again for this session
            if (
              !isNotInitialCheckAndReferredFromAPRO ||
              treatment.cb_session_expiration_check === 'on'
            ) {
              const expirationThreshold = parseInt(
                treatment.cb_session_expiration_threshold,
                10,
              );
              const useCreateTimeOrExpireTime = treatment.levity_use_createtime
                ? timeLeftUntilCbSessionTimeout()
                : timeLeftUntilCBSessionExpire();
              // hard override only used for debugging, should remain off, see FY-12447
              const expirationTimeDiff =
                treatment.cb_session_expiration_override ||
                useCreateTimeOrExpireTime;
              window.localStorage.setItem('apc:initialExpirationCheck', true);
              if (expirationTimeDiff < expirationThreshold) {
                window.sessionStorage.setItem(
                  'EXTERNAL_REFERRER_URL',
                  document.referrer,
                );
                authenticate();
              }
            }
          }
        },
      });

      flagHandler.invoke();

      // get the cb session as soon as app loads
      storeCbSession(getCbSession());
      storeApolloClient(this.props.client);
      authenticateStrategy(this.props);
    }

    shouldComponentUpdate(nextProps) {
      return (
        nextProps.currentState.rendering !==
          this.props.currentState.rendering ||
        nextProps.currentState.reloading !==
          this.props.currentState.reloading ||
        nextProps.userData !== this.props.userData ||
        nextProps.location.pathname !== this.props.location.pathname ||
        nextProps.location.search !== this.props.location.search
      );
    }

    /**
     * When we have me.initId, proceed with identifying the user in Mixpanel.
     */
    componentWillUpdate(nextProps) {
      /**
       * Allow us to keep track of the previous APC URL. This, initially, is
       * being used for the 404 page. So that we know where the user was just
       * before the 404 page was triggered.
       */
      if (
        nextProps.location.pathname !== this.props.location.pathname ||
        nextProps.location.search !== this.props.location.search
      ) {
        window.sessionStorage.setItem(
          'apc:locationFrom',
          `${this.props.location.pathname}${this.props.location.search}`,
        );
      }

      if (nextProps.userData.me && nextProps.currentState.rendering) {
        this.props.pageLoaded();
      }
    }

    render() {
      const { children, currentState, userData } = this.props;

      if (
        currentState.rendering ||
        currentState.reloading ||
        children === null
      ) {
        return (
          <div>
            <ToastContentWrapper />
            <Notifications />
            <ToastNotifications />
            <LoadingSpinner />
          </div>
        );
      } else if (!currentState.rendering) {
        return (
          <div>
            <ToastContentWrapper />
            <Notifications />
            <ToastNotifications />
            <WrappedComponent {...this.props} />
            <SecureFirewallCheck userData={userData} />
          </div>
        );
      }
    }
  }

  function mapStateToProps(state) {
    return {
      userData: state.userData,
      currentState: state.currentState,
    };
  }

  return compose(
    withApollo,
    withRouter,
    connect(mapStateToProps, {
      pageLoaded: pageLoading.fulfill,
      authorize,
      addSession,
      endSession,
      storeCbSession: storeCbSessionAction,
      levityLoginAction,
      levityLogoutAction,
      bindLevityEventsAction,
      authenticateStrategy: authenticateStrategyAction,
      storeApolloClient: storeApolloClientAction,
    }),
    ValidatedRoute,
  )(RequireAuth);
};
